import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/api/http';
import { tokenStore } from '@/lib/auth';
import { PublicHeader } from '@/components/public/public-header';
import { PublicFooter } from '@/components/public/public-footer';

export const Route = createFileRoute('/convite/$token')({ component: ConvitePage });

function ConvitePage() {
  const { token } = Route.useParams();
  const isLoggedIn = !!tokenStore.getAccess();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => api.get(`invitations/${token}`).json<any>(),
  });

  const accept = useMutation({
    mutationFn: () => api.post(`invitations/${token}/accept`).json<any>(),
  });

  return (
    <div className="bg-[#FBF8F3] min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1 max-w-2xl mx-auto px-6 py-16 w-full">
        {isLoading ? (
          <div className="bg-white rounded-3xl p-12 text-center animate-pulse">
            <div className="h-6 bg-slate-100 rounded w-2/3 mx-auto mb-4" />
            <div className="h-4 bg-slate-100 rounded w-1/2 mx-auto" />
          </div>
        ) : isError || !data ? (
          <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-sm">
            <div className="text-4xl mb-4">⚠️</div>
            <h1 className="text-xl font-bold text-slate-700 mb-2">Convite inválido ou expirado</h1>
            <p className="text-slate-500">Verifique o link com a pessoa que enviou o convite.</p>
            <Link to="/" className="mt-6 inline-block text-teal-600 hover:underline">
              ← Voltar para a home
            </Link>
          </div>
        ) : accept.isSuccess ? (
          <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-sm">
            <div className="text-4xl mb-4">🎉</div>
            <h1 className="text-2xl font-extrabold text-slate-800 mb-2">Convite aceito!</h1>
            <p className="text-slate-500 mb-6">
              Você agora está vinculado a <strong>{data.therapist?.user?.name}</strong>.
            </p>
            <Link
              to="/dashboard"
              className="inline-flex bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-full font-medium transition shadow"
            >
              Ir para o painel →
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-3xl p-10 border border-slate-100 shadow-sm">
            <div className="inline-flex items-center gap-2 bg-coral-50 border border-coral-100 rounded-full px-4 py-1.5 text-coral-600 text-sm font-medium mb-6">
              Convite do profissional
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-800 leading-tight">
              {data.therapist?.user?.name} convidou você para se tornar cliente na Nosso Ello
            </h1>
            <p className="text-slate-500 mt-3">
              Aceitando este convite você poderá agendar sessões, conversar pelo chat e acompanhar seu histórico no painel.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              {isLoggedIn ? (
                <button
                  onClick={() => accept.mutate()}
                  disabled={accept.isPending}
                  className="bg-teal-500 hover:bg-teal-600 text-white px-7 py-3 rounded-full font-medium transition shadow disabled:opacity-50"
                >
                  {accept.isPending ? 'Aceitando...' : 'Aceitar convite →'}
                </button>
              ) : (
                <>
                  <Link
                    to="/register"
                    search={{ invitation: token } as any}
                    className="bg-coral-400 hover:bg-coral-500 text-white px-7 py-3 rounded-full font-medium transition shadow"
                  >
                    Criar conta para aceitar
                  </Link>
                  <Link
                    to="/login"
                    search={{ next: `/convite/${token}` } as any}
                    className="border border-slate-200 hover:bg-slate-50 px-7 py-3 rounded-full font-medium text-slate-700 transition"
                  >
                    Já tenho conta
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </main>
      <PublicFooter />
    </div>
  );
}
